import React from 'react';
import axios from 'axios';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DownloadIcon from '@mui/icons-material/Download';
import '../../../styles/ACIPaymentReport.css';
import { refreshToken } from '../../../globals/GlobalFunctions';
import TextField from "@mui/material/TextField";

function ACIPaymentReport() {
    const [invoiceNumber, setInvoiceNumber] = React.useState<number | null>(null);
    const [loadingReport, setLoadingReport] = React.useState(false);

    const [validationError, setValidationError] = React.useState(false);
    const [validationText, setValidationText] = React.useState('');

    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState(false);
    const [errorText, setErrorText] = React.useState('There was an error generating SPEC report.');

    const token = localStorage.getItem('jwt');
    const baseURL = localStorage.getItem('baseURL') || '';

    const handleChange = React.useCallback((e: any) => setInvoiceNumber(e.target.value), []);

    const validateRequest = () => {
        if (!invoiceNumber || invoiceNumber < 1) {
            setValidationText('Invoice is required.');
            setValidationError(true);
            return false;
        }

        return true;
    };

    const generateACIPaymentReport = async () => {
        if (!validateRequest()) {
            return;
        }

        try {
            setLoadingReport(true);
            setErrorAlert(false);
            setSuccessAlert(false);

            refreshToken();

            const response = await axios.get(
                `${baseURL}reports/getACIPaymentReport/${invoiceNumber}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const { fileName, contentType, data } = response.data;

            const binaryString = atob(data);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);

            for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([bytes], { type: contentType });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);

            setSuccessAlert(true);
        } catch (error: any) {
            console.error('ACI Payment Report Error:', error);

            if (error?.response) {
                setErrorText(`Backend error: ${error.response.status} ${error.response.statusText}`);
            } else if (error?.request) {
                setErrorText('No response from backend. Make sure the API is running.');
            } else {
                setErrorText('There was an error generating the ACI Payment report.');
            }

            setErrorAlert(true);
        } finally {
            setLoadingReport(false);
        }
    };

    return (
        <div className="aciPaymentReportPageWrap">
            <div className="aciPaymentReportCard">
                <span className="aciPaymentReportTitle">
                    ACI Payment Remittance Report
                </span>

                <div className="aciPaymentReportField">
                    <span className="aciPaymentReportLabel">Invoice Number</span>

                    <TextField name="invoiceNumber" variant="outlined" className="aciPaymentInput" size="small"
                               type="number" value={invoiceNumber} onChange={handleChange}
                               onKeyDown={(event) => {
                                   if (!/[0-9]/.test(event.key) && event.key !== "Backspace" && event.key !== 'Enter' ) {
                                       event.preventDefault();
                                   }
                               }}/>
                </div>

                <LoadingButton
                    variant="contained"
                    onClick={generateACIPaymentReport}
                    loading={loadingReport}
                    startIcon={<DownloadIcon />}
                    className="aciPaymentReportButton"
                >
                    Generate ACI Payment Remittance
                </LoadingButton>

                {successAlert && (
                    <Alert severity="success" className="aciPaymentReportAlert">
                        <AlertTitle>Success</AlertTitle>
                        ACI Payment report request completed.
                    </Alert>
                )}

                {errorAlert && (
                    <Alert severity="error" className="aciPaymentReportAlert">
                        <AlertTitle>Error</AlertTitle>
                        {errorText}
                    </Alert>
                )}
            </div>

            <Dialog open={validationError} onClose={() => setValidationError(false)}>
                <DialogTitle>Missing Information</DialogTitle>
                <DialogContent>
                    <DialogContentText>{validationText}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setValidationError(false)}>OK</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default React.memo(ACIPaymentReport);